/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { getStrings } from "@microsoft/shifts-experiences-shared";
import { UserInfo } from "@microsoft/shifts-models-view";
import {
    UsersStore,
    usersActions
} from "@microsoft/shifts-states-openshifts";
import { setGlobalMessageFromException } from "sh-application";
import { getGenericEventPropertiesObject } from "sh-instrumentation";
import { InstrumentationService } from "sh-services";
import AllDataServicesInstance from "sh-services/dataservice/AllDataServicesInstance";

import { IUserPersonaProps } from "./IUserPersonaProps";

/**
 * Retrieves the team name for the user from the data service.
 * @param teamId The team id.
 */

/**
 * Sets user display name in 'text' prop when 'showUserNameFromUserId' is true.
 * @param props The properties.
 * @returns The updated properties with user display name set in 'text' prop when 'showUserNameFromUserId' is true.
 */
export const setUserNameInTextProp = (props: IUserPersonaProps): IUserPersonaProps => {
    const { showUserNameFromUserId, userId } = props;

    if (!userId || !showUserNameFromUserId) {
        return props;
    }

    const updatedProps = { ...props };
    const { valuesByIds } = UsersStore();
    const user = valuesByIds.get(userId);

    if (!user) {
        getUsersProfiles([userId]);
    } else {
        updatedProps.text = user.name ? user.name : getStrings("openShiftsExperience", "deletedUserNameText");
    }

    return updatedProps;
};

/**
 * Updates the persona text.
 * @param props The persona properties.
 * @param teamName The team name.
 * @returns The updated persona properties.
 */
const updatePersonaText = (props: Partial<IUserPersonaProps>, teamName?: string): Partial<IUserPersonaProps> => {
    if (teamName) {
        // Moves secondaryText to tertiaryText and set teamId as secondaryText.
        const { secondaryText } = props;
        props.tertiaryText = secondaryText;
        props.secondaryText = teamName;
    }

    return props;
};

/**
 * Updates the text props if team name is provided.
 * @param props The existing props.
 * @param teamName The team name.
 * @returns The updated props.
 */
export const updateTextProps = (props: Partial<IUserPersonaProps>, teamName?: string): Partial<IUserPersonaProps> => {
    if (!teamName) {
        return props;
    }

    const updatedTextProps = updatePersonaText(props, teamName);
    return { ...props, ...updatedTextProps };
 };

const logEvent = (eventType: string, isSuccess?: boolean): void => {
    const eventProps = [
        getGenericEventPropertiesObject(InstrumentationService.properties.EventType, eventType)
    ];

    if (typeof isSuccess == "boolean") {
        eventProps.push(getGenericEventPropertiesObject(InstrumentationService.properties.IsSuccess, isSuccess));
    }

    InstrumentationService.logEvent(InstrumentationService.events.UserPersona, eventProps);
};

const getUsersProfiles = async (userIds: string[]): Promise<UserInfo[]> => {
    const userProfileDataService = AllDataServicesInstance.UserProfileDataService;

    const setDataInState = (users: UserInfo[]): void => {
        const ids = users.map(({ id }) => id);

        usersActions.updateMap(users, ({ id }) => id);
        usersActions.loadSucceed(ids);
    };

    try {
        usersActions.loadStarted();

        const users = await userProfileDataService.getUsersProfiles(userIds, setDataInState);

        logEvent(InstrumentationService.values.UsersProfilesLoaded, true);
        return users;
    } catch (error) {
        logEvent(InstrumentationService.values.UsersProfilesLoaded, false);
        InstrumentationService.trackException(error, "UserPersona.mappers.getUsersProfiles");
        usersActions.loadFailed(error);
        setGlobalMessageFromException(error, false);
    }
};